import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import AnalyticsExpandableWrapper from '../Components/AnalyticsExpandableRow';
import Breadcrumbs from '../Components/Breadcrumbs';
import DateRangeSelector, { getDateRangeByPeriod } from '../Components/DateRangeSelector';
import '../Components/OrganizationDetail.css';
import Table from '../Components/Tables';
import '../Components/TotalAnalytics.css';
import { useChatAnalyticsApi } from '../Services/ChatAnalyticsApiProvider';
import { useOrganizationApi } from '../Services/OrganizationApiProvider';

function OrganizationContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { organizationId } = useParams();
  const { getOrganizationById } = useOrganizationApi();
  const { getTotalChatAnalytics, getDetailedChatAnalytics } = useChatAnalyticsApi();

  const [organization, setOrganization] = useState();
  const [analytics, setAnalytics] = useState([]);
  const [detailedAnalytics, setDetailedAnalytics] = useState([]);

  const alertShownRef = useRef(false);

  const [totalStartDate, setTotalStartDate] = useState();
  const [totalEndDate, setTotalEndDate] = useState();

  const [detailedStartDate, setDetailedStartDate] = useState();
  const [detailedEndDate, setDetailedEndDate] = useState();

  const [totalChannels, setTotalChannels] = useState(0);
  const [channelsPerPage, setChannelsPerPage] = useState(10);

  const columns = [
    { name: 'ID', selector: (row) => row.channel_id },
    { name: 'Channel Name', selector: (row) => row.channel_name },
    { name: 'Total Chats', selector: (row) => row.total_chats, sortable: true },
    { name: 'Active Chats', selector: (row) => row.active_chats, sortable: true },
    { name: 'Inactive Chats', selector: (row) => row.inactive_chats, sortable: true },
    { name: 'AI Chats', selector: (row) => row.communication_with_ai_message, sortable: true },
  ];

  useEffect(() => {
    if (location.state?.successMessage && !alertShownRef.current) {
      showAlert('success', location.state.successMessage);
      alertShownRef.current = true;
      window.history.replaceState({}, document.title);
    }
  }, [location, showAlert]);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const shouldSkipCache = !!location.state?.successMessage;
        const org = await getOrganizationById(organizationId, shouldSkipCache);
        setOrganization(org);
      } catch (error) {
        console.error('Error fetching organization:', error);
        navigate('/404');
      }
    };
    fetchOrganization();
  }, [organizationId, getOrganizationById, navigate]);

  useEffect(() => {
    const fetchChatAnalytics = async () => {
      let start = totalStartDate;
      let end = totalEndDate;

      if (!start) {
        const range = getDateRangeByPeriod('today');
        start = range.start_date;
        end = range.end_date;
      }
      try {
        const analyticsData = await getTotalChatAnalytics(start, end);
        setAnalytics(analyticsData);
      } catch (error) {
        console.error('Error fetching total analytics:', error);
      }
    };

    fetchChatAnalytics();
  }, [totalStartDate, totalEndDate, getTotalChatAnalytics]);

  const fetchDetailedAnalytics = async (skip = 0, limit = channelsPerPage) => {
    if (limit !== channelsPerPage) {
      setChannelsPerPage(limit);
    }
    let start = detailedStartDate;
    let end = detailedEndDate;

    if (!start) {
      const range = getDateRangeByPeriod('today');
      start = range.start_date;
      end = range.end_date;
    }
    try {
      const response = await getDetailedChatAnalytics(start, end, skip, limit);
      setDetailedAnalytics(response.analyticsData);
      setTotalChannels(response.items);
    } catch (error) {
      console.error('Error fetching detailed analytics:', error);
    }
  };

  useEffect(() => {
    fetchDetailedAnalytics(0, channelsPerPage);
  }, [detailedStartDate, detailedEndDate, getDetailedChatAnalytics]);

  const handlePeriodChange = useCallback((period) => {
    setTotalStartDate(period?.start_date);
    setTotalEndDate(period?.end_date);
  }, []);

  const handleDetailedPeriodChange = useCallback((period) => {
    setDetailedStartDate(period?.start_date);
    setDetailedEndDate(period?.end_date);
  }, []);

  if (!organization) return <div>Organization not found.</div>;
  if (!analytics) return <div>Loading analytics...</div>;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          <div className="org-details-header">
            <h2>Organization Details</h2>
            <button
              className="edit-btn"
              onClick={() => {
                navigate(`/organizations/${organizationId}/detail`);
              }}
              title="Edit parameter"
            >
              <i className="fa-solid fa-pencil"></i>
            </button>
          </div>

          <div className="org-header">
            <h2>{organization.title}</h2>
            <span className={`status-badge ${organization.is_active ? 'active' : 'inactive'}`}>
              {organization.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="detail-item">
            <label>ID:</label>
            <span>{organization.id}</span>
          </div>
          <div className="detail-item">
            <label>Description:</label>
            <p>{organization.description || 'No description available.'}</p>
          </div>
          <div className="detail-item">
            <label>Active Communications:</label>
            <p>{organization?.active_communications}</p>
          </div>
          <div className="detail-item">
            <label>Communications Per Day:</label>
            <p>{organization?.communications_per_day}</p>
          </div>
          <div className="detail-item">
            <label>Communications Per Month:</label>
            <p>{organization?.communications_per_month}</p>
          </div>
        </div>

        <div className="radius-yes p-2 bg-yes col-12">
          <h2 className="mb-4">Total Analytics</h2>
          <div className="analytics-grid">
            <div className="analytics-card">
              <div className="analytics-header">
                <h3>Total Chats</h3>
              </div>
              <div className="analytics-value">{analytics.total_chats?.toLocaleString() || 0}</div>
              <div className="analytics-details">
                <div className="detail-item">
                  <span className="label">Open chats</span>
                  <span className="value">{analytics.active_chats?.toLocaleString() || 0}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Chats with AI message</span>
                  <span className="value">
                    {analytics.communication_with_ai_message?.toLocaleString() || 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="analytics-card">
              <div className="analytics-header">
                <h3>Total Messages</h3>
              </div>
              <div className="analytics-value">
                {analytics.total_messages?.toLocaleString() || 0}
              </div>
              <div className="analytics-details">
                <div className="detail-item">
                  <span className="label">Incoming</span>
                  <span className="value">{analytics.input_messages?.toLocaleString() || 0}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Outgoing</span>
                  <span className="value">{analytics.output_messages?.toLocaleString() || 0}</span>
                </div>
              </div>
            </div>
            <div className="analytics-card">
              <div className="analytics-header">
                <h3>AI Statistics</h3>
              </div>
              <div className="analytics-details">
                <div className="detail-item">
                  <span className="label">Generated messages</span>
                  <span className="value">
                    {analytics.generated_messages?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">Sent messages</span>
                  <span className="value">{analytics.is_sent?.toLocaleString() || 0}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Redacted messages</span>
                  <span className="value">{analytics.is_redacted?.toLocaleString() || 0}</span>
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '6vh' }}>
            <DateRangeSelector storageKey="org-total" onChange={handlePeriodChange} />
          </div>
        </div>

        <div className="radius-yes p-2 bg-yes col-12">
          <h2>Channels</h2>
          {totalChannels === 0 ? (
            <p>No detailed data available.</p>
          ) : (
            <Table
              key={`${detailedStartDate}-${detailedEndDate}`}
              columns={columns}
              data={detailedAnalytics}
              selectableRowsHighlight
              expandableRows
              expandableRowsComponent={AnalyticsExpandableWrapper}
              paginationPerPage={channelsPerPage}
              paginationTotalRows={totalChannels}
              sortIcon={<i className="fa-solid fa-chevron-down" />}
              onChangePage={fetchDetailedAnalytics}
            />
          )}
          <DateRangeSelector storageKey="org-сhannel" onChange={handleDetailedPeriodChange} />
        </div>
      </div>
    </div>
  );
}

export default function Organization() {
  return <OrganizationContent />;
}
